import moment from "moment";
import {
  ANNOUNCEMENTS_ENABLED,
  ACTIVE_ANNOUNCEMENT,
  FeatureAnnouncement,
} from "./announcementConfig";

/**
 * Which announcement, if any, should be on screen - given the switch, the
 * configured announcement and the date.
 *
 * Takes all three rather than reading them, so the rules can be checked
 * directly instead of through whatever happens to be configured today.
 */
export const selectAnnouncement = (
  enabled: boolean,
  announcement: FeatureAnnouncement | null,
  now: moment.Moment = moment()
): FeatureAnnouncement | null => {
  if (!enabled || !announcement) {
    return null;
  }
  if (announcement.until) {
    const until = moment(announcement.until, moment.ISO_8601, true);
    // An unparseable date is a typo in a constant we control. Reading it as
    // "already expired" would hide the announcement silently, which is the one
    // failure nobody would notice; letting it run is visible and so gets fixed.
    if (until.isValid() && now.isAfter(until.endOf("day"))) {
      return null;
    }
  }
  return announcement;
};

/**
 * The announcement to show right now. Everything reads through this, never
 * ACTIVE_ANNOUNCEMENT directly, so the master switch cannot be bypassed.
 */
export const getActiveAnnouncement = (): FeatureAnnouncement | null =>
  selectAnnouncement(ANNOUNCEMENTS_ENABLED, ACTIVE_ANNOUNCEMENT);

/** The four values `account.status` can hold; anything else is treated as unknown. */
export type AccountStatus = "active" | "canceled" | "trial" | "trialExpired";

/**
 * Whether this announcement is for an account in this state.
 *
 * An unknown or missing status means the account has not loaded yet. That
 * counts as "not yet", never as "show it": guessing wrong in that direction
 * puts the announcement in front of the one audience it was configured to
 * leave out.
 */
export const isForAudience = (
  announcement: FeatureAnnouncement,
  status: string | undefined | null
): boolean => {
  if ((announcement.audience || "everyone") === "everyone") {
    return true;
  }
  return status === "active";
};

/**
 * Whether to offer the announcement in the profile menu.
 *
 * Deliberately NOT gated on the dismissals: someone who closed the banner is
 * exactly who this is for. Closing the banner used to take the announcement -
 * and the question it asks - away for good, because the CTA was the only way
 * in.
 *
 * A link-only announcement has nothing to open, so it gets no menu row; it
 * lives at its own URL and the banner is the way to it.
 */
export const shouldOfferAnnouncement = (
  announcement: FeatureAnnouncement | null,
  status: string | undefined | null
): boolean =>
  Boolean(
    announcement && announcement.dialog && isForAudience(announcement, status)
  );

const DISMISSED_STORAGE_KEY = "encharge-announcements-dismissed";

/**
 * The banner and the dialog are closed separately - someone can wave the
 * dialog away and still want the banner there to open it again later - so each
 * gets its own entry under the same announcement.
 */
export const DIALOG_SCOPE = "dialog";

// Keyed by team member as well as announcement: two people using the same
// browser should not inherit each other's dismissals.
const dismissalKey = (
  announcementId: string,
  teamMemberId: string | number,
  scope?: string
) => `${announcementId}${scope ? `:${scope}` : ""}:${teamMemberId}`;

const readDismissed = (): string[] => {
  try {
    const raw = localStorage.getItem(DISMISSED_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((entry) => typeof entry === "string")
      : [];
  } catch (e) {
    // unavailable (private mode) or corrupt - treat as "nothing dismissed"
    return [];
  }
};

export const isAnnouncementDismissed = (
  announcementId: string,
  teamMemberId: string | number,
  scope?: string
): boolean =>
  readDismissed().indexOf(dismissalKey(announcementId, teamMemberId, scope)) !==
  -1;

/**
 * Remembered per browser, not per account: this is a "you have already seen
 * this" flag, and getting it wrong costs one extra banner rather than any
 * data. Storing it server-side would make it follow the member between
 * devices, and needs a column - worth doing if we keep the loop, not for the
 * first one.
 */
export const rememberAnnouncementDismissed = (
  announcementId: string,
  teamMemberId: string | number,
  scope?: string
) => {
  try {
    const key = dismissalKey(announcementId, teamMemberId, scope);
    const dismissed = readDismissed();
    if (dismissed.indexOf(key) === -1) {
      dismissed.push(key);
      localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(dismissed));
    }
  } catch (e) {
    // see above - a banner that reappears next session is the whole downside
  }
};

export type { FeatureAnnouncement } from "./announcementConfig";
export {
  ANNOUNCEMENTS_ENABLED,
  ACTIVE_ANNOUNCEMENT,
} from "./announcementConfig";
